/* eslint-disable react-hooks/exhaustive-deps */

//#region Imports
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AssignmentOutlined as AssignmentOutlinedIcon,
  HistoryOutlined as HistoryOutlinedIcon,
} from '@mui/icons-material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faImage,
  faBan,
  faPlus,
  faChevronLeft,
  faPaperPlane,
  faArrowsRotate,
} from '@fortawesome/free-solid-svg-icons';
import {
  GoogleMap,
  InfoBox,
  Marker,
  useJsApiLoader,
} from '@react-google-maps/api';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import customAxios from '../../utils/customAxios';

import '../../scss/Pages/_visitDetails.scss';
import variables from '../../scss/_variables.scss';
import { GlobalVariables } from '../../globalVar';
import { useDownloadReport } from '../../hooks/UseDownloadReport';
import moment from 'moment';
import {
  faAlignLeft,
  faClose,
  faFileDownload,
  faTableRows,
  faTimer,
} from '@fortawesome/pro-regular-svg-icons';
import { useTranslationHook } from '../../hooks/UseTranslationHook';

import VisitLogs from './Tabs/VisitLogs';
import VisitReports from './Tabs/VisitReports';
import VisitImages from './Tabs/VisitImages';

import ModalCancelVisit from '../../components/Modal/ModalCancelVisit';
import ModalDetailViewAssignTech from '../../components/Modal/ModalDetailViewAssignTech';
import useFetchTemplateAndInputs from '../../hooks/UseFetchTemplateAndInputs';
import { getToast } from '../../utils/toastConfig';
import SendVisitModal from '../../components/Modal/Sigma/Visits/SendVisitModal';
import ChangeStatusModal from '../../components/Modal/Sigma/Visits/ChangeStatusModal';
import SlaLogsModal from '../../components/Modal/Sigma/Visits/SlaLogsModal';
import ResendVisitModal from '../../components/Modal/Sigma/Visits/ResendVisitModal';
import VisitConcat from './Tabs/VisitConcat';
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined';
//#endregion

const VisitDetails = () => {
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [visitDetails, setVisitDetails] = useState({});
  const [Logs, setLogs] = useState([]);
  const [freeTechs, setFreeTechs] = useState([]);
  const [isTechAssigned, setIsTechAssigned] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [visitReport, setVisitReport] = useState([]);

  const [modalAssignTechShow, setModalAssignTechShow] = useState(false);
  const [showSendVisitModal, setShowSendVisitModal] = useState(false);
  const [showResendVisitModal, setShowResendVisitModal] = useState(false);
  const [showChangeStatusModal, setShowChangeStatusModal] = useState(false);
  const [showSlaLogs, setShowSlaLogs] = useState(false);

  const [activeTab, setActiveTab] = useState('section1');

  const {
    visitsCardSiteStr,
    visitsCardAddressStr,
    visitsCardStatusStr,
    visitsCardTechStr,
    visitsCardNonAStr,
    visitsLogsTitleStr,
    visitsLogsEmptyStr,
  } = useTranslationHook();

  const {
    showReportViewer,
    reportTemplate,
    reportInputs,
    fetchTemplateAndInputs,
    closeReportViewer,
  } = useFetchTemplateAndInputs();

  useEffect(() => {
    if (activeTab === 'section3') {
      fetchTemplateAndInputs(id);
    }
  }, [activeTab]);

  const [modalCancelVisitShow, setModalCancelVisitShow] = useState(false);

  const { downloadReport } = useDownloadReport();

  const navigate = useNavigate();

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const responseVisitDetails = await customAxios.get(
        `${GlobalVariables.getURL()}visitDetails/${id}`,
        {
          headers: {
            Authorization: 'bearer ' + localStorage.getItem('token'),
          },
        }
      );
      const responseFreeTechs = await customAxios.get(
        `${GlobalVariables.getURL()}freeTechs`,
        {
          headers: {
            Authorization: 'bearer ' + localStorage.getItem('token'),
          },
        }
      );

      const responseVisitPhotos = await customAxios.get(
        `${GlobalVariables.getURL()}getVisitPhotos/${id}`,
        {
          headers: {
            Authorization: 'bearer ' + localStorage.getItem('token'),
          },
        }
      );

      const groupedLogsByDate = responseVisitDetails.data.visit_logs.reduce(
        (acc, log) => {
          const date = moment(log.updated_at).format('YYYY-MM-DD');
          const time = new Date(log.updated_at).toLocaleTimeString(undefined, {
            hour: '2-digit',
            minute: '2-digit',
          });

          if (!acc[date]) acc[date] = [];
          acc[date].push({ time, reason: log.updated_reason });
          return acc;
        },
        {}
      );
      setFreeTechs(responseFreeTechs.data);
      setVisitDetails({
        ...responseVisitDetails.data,
        images: responseVisitPhotos.data.data,
      });

      setLogs(groupedLogsByDate);
      setIsLoading(false);
    } catch (err) {
      console.log('🚀 ~ err:', err);
    }
  };

  function cancelVisit(visitInfo) {
    var visitId = visitInfo.visit_id;
    localStorage.setItem('visitIdToCancel', visitId);

    var visitClientName = visitInfo.client_name;
    var visitSiteName = visitInfo.site_name;
    var visitTypeName = visitInfo.visit_type;

    localStorage.setItem('visitClientNameForCancel', visitClientName);
    localStorage.setItem('visitSiteNameForCancel', visitSiteName);
    localStorage.setItem('visitTypeNameForCancel', visitTypeName);
  }

  const fetchReportData = async () => {
    try {
      const responseVisitReport = await customAxios.get(
        `${GlobalVariables.getURL()}getReport/${id}`,
        {
          headers: {
            Authorization: 'bearer ' + localStorage.getItem('token'),
          },
        }
      );

      setVisitReport(responseVisitReport.data.data.answers);
    } catch (err) {}
  };

  useEffect(() => {
    fetchData();
    fetchReportData();
  }, []);

  useEffect(() => {
    if (currentPage === 2) {
      fetchTemplateAndInputs(id);
    }
  }, [currentPage]);

  const handleTechAssigned = () => {
    setIsTechAssigned(true); // Update the state
    fetchData(); // Fetch data again
  };

  const handleAssignTechClick = () => {
    setModalAssignTechShow(true);
  };

  const handleAction = (actionId, visitId) => {
    if (actionId === 2 || actionId === 4 || actionId === 15) {
      cancelVisit(visitDetails);
      setModalCancelVisitShow(true);
    } else if (actionId === 17) {
      try {
        customAxios.patch(
          `${GlobalVariables.getURL()}ackVisit/${visitId}`,
          {},
          {
            headers: {
              Authorization: 'bearer ' + localStorage.getItem('token'),
            },
          }
        );
        getToast({ status: 'success', title: 'Razón aceptada exitosamente.' });
        window.location.reload();
      } catch (error) {}
    }
  };

  //#region Map Region
  const mapRef = useRef(null);

  const onLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  const mapOptions = useMemo(
    () => ({
      clickableIcons: false,
      fullscreenControl: false,
      mapTypeControl: false,
      streetViewControl: false,
    }),
    []
  );

  const Map = () => {
    const { isLoaded } = useJsApiLoader({
      googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    });

    if (!isLoaded) {
      return <></>;
    }

    return (
      <GoogleMap
        ref={mapRef}
        center={{
          lat: visitDetails.visit_location.lat,
          lng: visitDetails.visit_location.long,
        }}
        zoom={15}
        onLoad={onLoad}
        mapContainerClassName="visitDetailsMap"
        options={mapOptions}
      >
        <InfoBox
          position={{
            lat: visitDetails.visit_location.lat,
            lng: visitDetails.visit_location.long,
          }}
          options={{
            closeBoxURL: ``,
            enableEventPropagation: true,
            alignBottom: true,
            pixelOffset: new window.google.maps.Size(0, -10),
          }}
        >
          <Box
            sx={{
              backgroundColor: 'white',
              padding: '10px',
              borderRadius: '10px',
            }}
            width="100%"
          >
            <Box sx={{ fontSize: '1rem', width: '100%' }}>
              <Typography fontWeight="bold">
                {visitDetails.site_name}
              </Typography>
            </Box>
          </Box>
        </InfoBox>
        <Marker
          position={{
            lat: visitDetails.visit_location.lat,
            lng: visitDetails.visit_location.long,
          }}
          icon={{
            path: 'M215.7 499.2C267 435 384 279.4 384 192C384 86 298 0 192 0S0 86 0 192c0 87.4 117 243 168.3 307.2c12.3 15.3 35.1 15.3 47.4 0zM192 128a64 64 0 1 1 0 128 64 64 0 1 1 0-128z',
            fillColor: 'red',
            fillOpacity: 1,
            strokeWeight: 0,
            scale: 0.06,
          }}
        />
      </GoogleMap>
    );
  };

  //#endregion

  const VisitDetailsCard = () => {
    return (
      <Box pt={0} pb={2} pl={1} pr={2} borderBottom={1} borderColor="grey.300">
        <Grid container alignItems="stretch" justifyContent="space-between">
          <Grid item xs={12} pt={0}>
            <Box display="flex" alignItems="center">
              <Button
                sx={{ display: 'flex' }}
                onClick={() => {
                  navigate(-1);
                }}
              >
                <FontAwesomeIcon
                  icon={faChevronLeft}
                  style={{ marginRight: '5%' }}
                />
                Volver
              </Button>
            </Box>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h4">
                <span style={{ color: variables.redDabi }}>
                  {' ' + visitDetails.visit_id}
                </span>
                <span>{' ' + visitDetails.visit_type}</span>
                <span>{' | ' + visitDetails.client_name}</span>
              </Typography>
              <Typography variant="body1" component="div" align="right">
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="flex-end"
                >
                  <span style={{ color: 'gray', fontSize: '0.9em' }}>
                    {new Date(visitDetails.visit_date).toLocaleDateString(
                      undefined,
                      {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric',
                      }
                    )}
                  </span>
                  <span>
                    {new Date(visitDetails.visit_date)
                      .toLocaleTimeString(undefined, {
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                      .toUpperCase()}
                  </span>
                </Box>
              </Typography>
            </Box>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Grid item xs={2.6}>
                <span style={{ color: 'gray', fontSize: '0.9em' }}>
                  Estatus:
                </span>
                <br />
                <span title={visitDetails.visit_status}>
                  {visitDetails.final_status_name}
                </span>
                {visitDetails.final_status_id !== 16 &&
                visitDetails.final_status_id !== 18 ? (
                  <Tooltip title="Cambiar estatus">
                    <Button
                      style={{
                        color: '#E26A00',
                        paddingBottom: '10px',
                        height: '26px',
                      }}
                      onClick={() => {
                        setShowChangeStatusModal(true);
                      }}
                    >
                      <ModeEditOutlineOutlinedIcon
                        style={{
                          height: '20px',
                          width: '20px',
                        }}
                      />
                    </Button>
                  </Tooltip>
                ) : null}
              </Grid>
              <Grid item xs={2.2}>
                <span style={{ color: 'gray', fontSize: '0.9em' }}>
                  {visitsCardSiteStr}
                </span>
                <br />
                <span
                  className="span-limit-text"
                  title={visitDetails.site_name}
                >
                  {visitDetails.site_name}
                </span>
              </Grid>
              <Grid item xs={4.2}>
                <span style={{ color: 'gray', fontSize: '0.9em' }}>
                  {visitsCardAddressStr}
                </span>
                <br />
                <span title={visitDetails.address}>{visitDetails.address}</span>
              </Grid>

              <Grid item xs={4.4} sx={{ pl: 3 }}>
                <span style={{ color: 'gray', fontSize: '0.9em' }}>
                  {visitsCardTechStr}
                </span>
                <br />
                <span>
                  {visitDetails.full_name
                    ? visitDetails.full_name
                    : visitsCardNonAStr}
                </span>
                {
                  visitDetails.full_name ? (
                    <Button
                      style={{ color: '#E26A00' }}
                      onClick={() =>
                        navigate(`/tech/details/${visitDetails.tech_id}`)
                      }
                    >
                      •••
                    </Button>
                  ) : null
                  // (
                  //   <Button
                  //     style={{ color: '#E26A00' }}
                  //     onClick={handleAssignTechClick}
                  //   >
                  //     <FontAwesomeIcon icon={faPlus} />
                  //   </Button>
                  // )
                }
              </Grid>
              <Grid item xs={4} display="flex" alignItems="center">
                <Grid container alignItems="center">
                  <Box
                    flex={1}
                    display="flex"
                    justifyContent="flex-end"
                    gap={1}
                  >
                    <Button
                      sx={{ padding: '10px', width: '100%' }}
                      variant="outlined"
                      onClick={() => setShowSlaLogs(true)}
                    >
                      <span style={{ marginRight: '10px' }}>
                        <FontAwesomeIcon icon={faTimer} />
                      </span>
                      Tiempos
                    </Button>

                    {!visitDetails.tech_id ? (
                      <Button
                        sx={{ padding: '10px', width: '100%' }}
                        variant="outlined"
                        onClick={() => setShowSendVisitModal(true)}
                      >
                        <span style={{ marginRight: '10px' }}>
                          <FontAwesomeIcon icon={faPaperPlane} />
                        </span>
                        Enviar Servicio
                      </Button>
                    ) : null}

                    {/*ONLY SHOW BUTTON WHEN A TECH IS ALREADY ASSIGNED AND THE CURRENT TIMED_ACTION IS NOT CANCELED OR PENDING SEND VISIT OR COMPLETED */}
                    {![1, 4, 5, 9, 10, 13, 14, 16, 18].includes(
                      visitDetails.final_status_id
                    ) && visitDetails.tech_id ? (
                      <Button
                        sx={{ padding: '10px', width: '100%' }}
                        variant="outlined"
                        onClick={() => setShowResendVisitModal(true)}
                      >
                        <span style={{ marginRight: '10px' }}>
                          <FontAwesomeIcon icon={faArrowsRotate} />
                        </span>
                        Reenviar Servicio
                      </Button>
                    ) : null}

                    {!(visitDetails.vs_id === 7 || visitDetails.vs_id === 1) ? (
                      <Button
                        sx={{ padding: '10px', width: '100%' }}
                        variant="outlined"
                        onClick={() => setModalCancelVisitShow(true)}
                      >
                        <span style={{ marginRight: '10px' }}>
                          <FontAwesomeIcon icon={faBan} />
                        </span>
                        Cancelar Servicio
                      </Button>
                    ) : (
                      <></>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const VisitDetailsLogs = () => {
    const questionsUpdated = groupQuestions(visitReport);

    return (
      <>
        <Box pt={0} pl={1} pr={2}>
          <Grid container alignItems="stretch" justifyContent="left">
            <Grid item xs={12} mx={1}>
              <Box
                borderBottom={1}
                borderColor="grey.300"
                display="flex"
                justifyContent="flex-start"
              >
                <Tabs
                  value={activeTab}
                  onChange={(event, newValue) => setActiveTab(newValue)}
                  sx={{
                    '& .MuiTabs-indicator': {
                      backgroundColor: variables.redDabi,
                    },
                    '& .Mui-selected': {
                      color: `${variables.redDabi} !important`,
                    },
                  }}
                >
                  <Tab
                    sx={{
                      justifyContent: 'space-evenly',
                      '& .fa-table-rows': {
                        color:
                          activeTab === 'section1'
                            ? variables.redDabi
                            : '#666666',
                      },
                    }}
                    label="Concatenado"
                    value="section1"
                    icon={
                      <FontAwesomeIcon
                        icon={faAlignLeft}
                        style={{ height: '20px', width: '20px' }}
                      />
                    }
                  />
                  <Tab
                    sx={{
                      justifyContent: 'space-evenly',
                      '& .fa-table-rows': {
                        color:
                          activeTab === 'section2'
                            ? variables.redDabi
                            : '#666666',
                      },
                    }}
                    label="Bitácora"
                    value="section2"
                    icon={
                      <FontAwesomeIcon
                        icon={faTableRows}
                        style={{ height: '20px', width: '20px' }}
                      />
                    }
                  />
                  {visitReport.length > 0 && ( // Check if there are report
                    <Tab
                      sx={{
                        justifyContent: 'space-evenly',
                        color:
                          activeTab === 'section3'
                            ? variables.redDabi
                            : '#666666',
                      }}
                      label="Reporte"
                      value="section3"
                      icon={
                        <AssignmentOutlinedIcon
                          sx={{
                            height: '22px',
                            width: '22px',
                            color:
                              activeTab === 'section3'
                                ? variables.redDabi
                                : '#666666',
                          }}
                        />
                      }
                    />
                  )}
                  {visitDetails.images &&
                    visitDetails.images.length > 0 && ( // Check if there are images
                      <Tab
                        sx={{
                          justifyContent: 'space-evenly',
                          '& .fa-image': {
                            color:
                              activeTab === 'section4'
                                ? variables.redDabi
                                : '#666666',
                          },
                        }}
                        label="Imágenes"
                        value="section4"
                        icon={
                          <FontAwesomeIcon
                            icon={faImage}
                            style={{ height: '20px', width: '20px' }}
                          />
                        }
                      />
                    )}
                </Tabs>
              </Box>
            </Grid>
          </Grid>
        </Box>
        {activeTab === 'section1' && <VisitConcat visitId={id} />}
        {activeTab === 'section2' && (
          <VisitLogs
            Logs={Logs}
            visitsLogsTitleStr={visitsLogsTitleStr}
            visitsLogsEmptyStr={visitsLogsEmptyStr}
          />
        )}
        {activeTab === 'section3' && (
          //TODO: Check if when having a report it works
          <>
            <VisitReports
              questionsUpdated={questionsUpdated}
              fetchTemplateAndInputs={fetchTemplateAndInputs}
              id={id}
              reportTemplate={reportTemplate}
              reportInputs={reportInputs}
              showReportViewer={showReportViewer}
              closeReportViewer={closeReportViewer}
              downloadReport={downloadReport}
            />
          </>
        )}
        {activeTab === 'section4' && (
          <VisitImages images={visitDetails.images || []} />
        )}
      </>
    );
  };

  const groupQuestions = (question) => {
    return question.reduce((acc, question) => {
      if (!acc[question.question_number]) {
        acc[question.question_number] = [];
      }
      acc[question.question_number].push(question);
      return acc;
    }, {});
  };

  return (
    <div className="rootContainerVisits">
      {isLoading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          paddingY={10}
          height="100%"
        >
          <CircularProgress />
        </Box>
      ) : visitDetails !== null ? (
        // MAIN GRID CONTAINER - Primary layout container
        <Grid
          container
          className="detailsContainer"
          flexDirection="column"
          flexWrap="nowrap"
        >
          {/* MODAL SECTION - All popup/overlay components */}
          <ModalCancelVisit
            show={modalCancelVisitShow}
            visitId={visitDetails.visit_id}
            onHide={() => setModalCancelVisitShow(false)}
          />

          <ModalDetailViewAssignTech
            show={modalAssignTechShow}
            onHide={() => setModalAssignTechShow(false)}
            visitId={id}
            freeTechs={freeTechs}
            clientName={visitDetails.client_name}
            siteName={visitDetails.site_name}
            visitTypeName={visitDetails.visit_type}
            setIsTechAssigned={setIsTechAssigned}
          />

          <SendVisitModal
            visitId={visitDetails.visit_id}
            showModal={showSendVisitModal}
            setShowModal={setShowSendVisitModal}
            refreshData={() => {
              fetchData();
              fetchReportData();
            }}
          />

          <ResendVisitModal
            visitId={visitDetails.visit_id}
            showModal={showResendVisitModal}
            setShowModal={setShowResendVisitModal}
            refreshData={() => {
              fetchData();
              fetchReportData();
            }}
          />

          <SlaLogsModal
            visitId={visitDetails.visit_id}
            showModal={showSlaLogs}
            setShowModal={setShowSlaLogs}
            refreshData={() => {
              fetchData();
              fetchReportData();
            }}
          />

          <ChangeStatusModal
            visitId={visitDetails.visit_id}
            showModal={showChangeStatusModal}
            setShowModal={setShowChangeStatusModal}
            refreshData={() => {
              fetchData();
              fetchReportData();
            }}
          />

          {/* HEADER SECTION - Visit details card at the top */}
          <Grid item xs="auto" width="100%">
            <VisitDetailsCard />
          </Grid>

          {/* CONTENT SECTION - Main content area below header */}
          <Grid item xs height="100%">
            {/* HORIZONTAL SPLIT CONTAINER - Splits content left/right */}
            <Grid container height="100%">
              {/* LEFT SIDE - Tabs section (xs=8 = 66.67% width) */}
              <Grid item xs={8} sx={{ height: '100% !important' }}>
                {/* TABS WRAPPER 1 - First wrapper box (height: 90%) */}
                <Box sx={{ height: '90%' }}>
                  {/* TABS WRAPPER 2 - Second wrapper box (height: 100%) */}
                  <Box sx={{ height: '80%' }}>
                    <VisitDetailsLogs />
                  </Box>
                </Box>
              </Grid>

              {/* RIGHT SIDE - Map section (xs=4 = 33.33% width) */}
              <Grid item xs={4}>
                <Map />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      ) : (
        <></>
      )}
    </div>
  );
};

export default VisitDetails;
